/** Requests page — viewer creates + views their own reschedule / membership
 * cancellation / unenroll requests (Prompt 11E.5, build item 3).
 *
 * Create-request-only: this page NEVER writes directly to a session,
 * membership, or student record — every submit creates a request doc that
 * an admin approves/denies via the approval queue.
 */

import React, { useState } from 'react';
import { usePortalStudents } from '../api/portal';
import {
  useCreateRequest,
  useMyRequests,
  type RequestType,
} from '../api/requests';

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '0.5rem',
  padding: '0.875rem 1.25rem',
  marginBottom: '0.5rem',
};

const statusColor: Record<string, string> = {
  pending: '#f59e0b',
  approved: '#16a34a',
  denied: '#ef4444',
};

const RequestsPage: React.FC = () => {
  const { data: studentsData } = usePortalStudents();
  const { data: requestsData, isLoading } = useMyRequests();
  const createRequest = useCreateRequest();

  const students = studentsData?.items ?? [];
  const requests = requestsData?.items ?? [];

  const [type, setType] = useState<RequestType>('reschedule');
  const [studentId, setStudentId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [proposedStart, setProposedStart] = useState('');
  const [membershipId, setMembershipId] = useState('');
  const [note, setNote] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!studentId) return;
    createRequest.mutate(
      {
        type,
        student_id: studentId,
        note: note || undefined,
        session_id: type === 'reschedule' ? sessionId : undefined,
        proposed_start:
          type === 'reschedule' && proposedStart
            ? new Date(proposedStart).toISOString()
            : undefined,
        membership_id: type === 'membership_cancel' ? membershipId : undefined,
      },
      {
        onSuccess: () => {
          setSessionId('');
          setProposedStart('');
          setMembershipId('');
          setNote('');
        },
      },
    );
  };

  return (
    <div data-testid="candeza-portal-requests-page">
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1.25rem' }}>Requests</h1>

      <form
        onSubmit={handleSubmit}
        style={{ ...card, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
        data-testid="candeza-portal-request-form"
      >
        <label>
          Request type
          <select
            value={type}
            onChange={(e) => setType(e.target.value as RequestType)}
            data-testid="candeza-portal-request-type"
            style={{ display: 'block', width: '100%' }}
          >
            <option value="reschedule">Request reschedule</option>
            <option value="membership_cancel">Request membership cancellation</option>
            <option value="unenroll">Request unenroll</option>
          </select>
        </label>

        <label>
          Student
          <select
            value={studentId}
            onChange={(e) => setStudentId(e.target.value)}
            required
            data-testid="candeza-portal-request-student"
            style={{ display: 'block', width: '100%' }}
          >
            <option value="">Select a student…</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>
                {s.first_name} {s.last_name}
              </option>
            ))}
          </select>
        </label>

        {type === 'reschedule' && (
          <>
            <label>
              Session ID (pick from Sessions tab)
              <input
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
                required
                data-testid="candeza-portal-request-session-id"
                style={{ display: 'block', width: '100%' }}
              />
            </label>
            <label>
              Proposed new date/time
              <input
                type="datetime-local"
                value={proposedStart}
                onChange={(e) => setProposedStart(e.target.value)}
                required
                data-testid="candeza-portal-request-proposed-start"
                style={{ display: 'block', width: '100%' }}
              />
            </label>
          </>
        )}

        {type === 'membership_cancel' && (
          <label>
            Membership ID
            <input
              value={membershipId}
              onChange={(e) => setMembershipId(e.target.value)}
              required
              data-testid="candeza-portal-request-membership-id"
              style={{ display: 'block', width: '100%' }}
            />
          </label>
        )}

        <label>
          Note (optional)
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="candeza-portal-request-note"
            style={{ display: 'block', width: '100%' }}
          />
        </label>

        <button
          type="submit"
          disabled={createRequest.isPending}
          data-testid="candeza-portal-request-submit"
        >
          {createRequest.isPending ? 'Submitting…' : 'Submit request'}
        </button>
      </form>

      <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '1.5rem 0 0.75rem' }}>
        My requests
      </h2>
      {isLoading ? (
        <div data-testid="candeza-portal-requests-loading">Loading…</div>
      ) : requests.length === 0 ? (
        <p data-testid="candeza-portal-requests-empty" style={{ color: '#6b7280' }}>
          No requests yet.
        </p>
      ) : (
        <div data-testid="candeza-portal-requests-list">
          {requests.map((r) => (
            <div key={r.id} style={card} data-testid={`candeza-portal-request-row-${r.id}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong style={{ textTransform: 'capitalize' }}>{r.type.replace('_', ' ')}</strong>
                <span
                  style={{
                    color: statusColor[r.status] ?? '#6b7280',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                  }}
                >
                  {r.status}
                </span>
              </div>
              {r.note && <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{r.note}</div>}
              {r.decision_note && (
                <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: 4 }}>
                  Admin note: {r.decision_note}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RequestsPage;

